import { useApolloClient, useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

import {
  Acknowledge,
  Box,
  Button,
  CardBox,
  Container,
  Link,
  NotificationDialog,
  Row,
  SelfAdvisorConfirmDialog,
  Stack,
  Text,
  useAlert,
} from '../../../components'
import * as SecuritiesPayment from '../../../components/payment/securities'
import { mutations, queries } from '../../../graphql'
import { fromAmountField, getTrackingData } from '../../../utils'
import { openApiFileWithData } from '../../../utils/open-api-file'
import { useAuth } from '../../../hooks'

export function Securities() {
  const client = useApolloClient()
  const { fundId } = useParams()
  const navigate = useNavigate()
  const [{ isImpersonating, impersonationType }] = useAuth()
  const isAdvisorImpersonating = isImpersonating && impersonationType === 'ADVISOR'

  const { data: { me: { fund } = { fund: {} } } = {} } = useQuery(queries.funds.myFund, {
    variables: { fundId: Number(fundId) },
    skip: !fundId,
  })
  const { data: { me } = {}, loading } = useQuery(queries.user.me)
  const { data: custodianSettingsData, loading: custodianSettingsLoading } = useQuery(
    queries.user.myAdvisorCustodianSettings,
    {
      skip: !isAdvisorImpersonating,
    }
  )
  const { data: requestedSecuritiesData, loading: requestedSecuritiesLoading } = useQuery(
    queries.funds.myRequestedSecuritiesContributions,
    {
      variables: { fundId: Number(fundId) },
      skip: !isAdvisorImpersonating,
    }
  )
  const advisorCustodianSettings = custodianSettingsData?.myAdvisorCustodianSettings

  const { profile } = me ?? {}
  const { number } = fund
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const [formData, setFormData] = useState({})
  const [selectedPath, setSelectedPath] = useState(null)
  const {
    register,
    handleSubmit,
    formState,
    getValues,
    setValue,
    watch,
    reset: resetForm,
    control,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      address: { province: '' },
      intendedUse: '',
      securities: [{ securitiesDescription: '', fundSymbolCUSIP: '', quantity: 0, amount: 0 }],
      pageLanguageConfirmed: false,
    },
  })

  const { isSubmitting, errors = {} } = formState
  const [dialogOpen, setDialogOpen] = useState(false)
  const [formReady, setFormReady] = useState(false)

  const { address = {} } = {}
  const province = watch('address.province')
  const intendedUse = watch('intendedUse')
  useEffect(() => {
    setValue('address.province', getValues('address.province'))
  }, [province])

  // Clear investment account fields when switching away from "invest"
  useEffect(() => {
    if (intendedUse !== 'invest') {
      setValue('investmentAccountNumber', '')
      setValue('investmentAccountName', '')
    }
  }, [intendedUse, setValue])

  // Pre-fill form with advisor's custodian settings when advisor is impersonating
  useEffect(() => {
    if (isAdvisorImpersonating && !custodianSettingsLoading) {
      if (advisorCustodianSettings) {
        resetForm({
          custodianName: advisorCustodianSettings.custodianName || '',
          advisorName: advisorCustodianSettings.advisorName || '',
          advisorEmail: advisorCustodianSettings.advisorEmail || '',
          advisorPhone: advisorCustodianSettings.advisorPhone || '',
          advisorDealerRepCode: advisorCustodianSettings.advisorDealerRepCode || '',
          address: {
            lineOne: advisorCustodianSettings.address?.lineOne || '',
            city: advisorCustodianSettings.address?.city || '',
            province: advisorCustodianSettings.address?.province || '',
            postalCode: advisorCustodianSettings.address?.postalCode || '',
          },
          intendedUse: '',
          securities: [{ securitiesDescription: '', fundSymbolCUSIP: '', quantity: 0, amount: 0 }],
          pageLanguageConfirmed: false,
        })
      }
      setFormReady(true)
    }
  }, [isAdvisorImpersonating, custodianSettingsLoading, advisorCustodianSettings, resetForm])

  const pageLanguageConfirmed = watch('pageLanguageConfirmed')

  const [letterOfDirectionPdf, setLetterOfDirectionPdf] = useState(null)
  const [advisorDialogOpen, setAdvisorDialogOpen] = useState(false)
  const [submittedIntendedUse, setSubmittedIntendedUse] = useState(null)
  const [errorDialogMessage, setErrorDialogMessage] = useState(null)
  const [existingContributionDialogOpen, setExistingContributionDialogOpen] = useState(false)
  const [selfAdvisorData, setSelfAdvisorData] = useState(null)

  useEffect(() => {
    if (!requestedSecuritiesLoading && requestedSecuritiesData?.myRequestedSecuritiesContributions?.length > 0) {
      setExistingContributionDialogOpen(true)
    }
  }, [requestedSecuritiesLoading, requestedSecuritiesData])

  // Path A: Advisor path - create a single 'requested' contribution
  const createAdvisorContributionRequest = async (data) => {
    try {
      const trackingData = getTrackingData()
      const amount = parseFloat(fromAmountField(data.intendedAmount))

      const deliveringCustodianInfo = {
        securitiesPath: 'advisor',
        custodianName: data.custodianName,
        advisorName: data.advisorName,
        advisorEmail: data.advisorEmail,
        intendedUse: data.intendedUse,
      }

      await client.mutate({
        mutation: mutations.contributions.createContributionRecord,
        variables: {
          fundId: Number(fundId),
          amount,
          paymentType: 4, // securities
          state: 7, // requested
          description: 'Advisor-Assisted Investment Contribution',
          taxReceipted: true,
          ...trackingData,
          deliveringCustodianInfo,
        },
        refetchQueries: [
          { query: queries.funds.myFund, variables: { fundId: Number(fundId) } },
          { query: queries.funds.myFundActivity, variables: { fundId: Number(fundId) } },
        ],
        awaitRefetchQueries: true,
      })

      setSubmittedIntendedUse(data.intendedUse)
      setAdvisorDialogOpen(true)
    } catch (e) {
      const msg = e.message ?? ''
      if (msg.includes('cannot be invited to the Advisor Portal')) {
        setErrorDialogMessage(
          'This email address cannot be invited to the Advisor Portal. Please try another email address or reach out to support@givewise.ca for assistance.'
        )
      } else {
        setAlert({
          message: `Failed to create securities contribution request: ${msg}`,
          error: msg,
          severity: 'error',
        })
      }
    }
  }

  // Create contribution records for each security (used by self-directed and advisor-led flows)
  const createContributionRecords = async (data, securitiesPath = 'self-directed') => {
    try {
      if (data && Array.isArray(data.securities)) {
        const trackingData = getTrackingData()
        // Build deliveringCustodianInfo from form data
        const deliveringCustodianInfo = {
          ...(securitiesPath ? { securitiesPath } : {}),
          accountNumber: data.accountNumber,
          custodianName: data.custodianName,
          advisorName: data.advisorName,
          advisorDealerRepCode: data.advisorDealerRepCode,
          advisorEmail: data.advisorEmail,
          advisorPhone: data.advisorPhone,
          address: data.address,
          ...(data.intendedUse ? { intendedUse: data.intendedUse } : {}),
          ...(data.investmentAccountNumber ? { investmentAccountNumber: data.investmentAccountNumber } : {}),
          ...(data.investmentAccountName ? { investmentAccountName: data.investmentAccountName } : {}),
        }

        const promises = data.securities.map((sec) =>
          client.mutate({
            mutation: mutations.contributions.createContributionRecord,
            variables: {
              fundId: Number(fundId),
              amount: Number(sec.amount),
              paymentType: 4,
              description:
                `${sec.securitiesDescription} (${sec.fundSymbolCUSIP}) - ${sec.quantity} Units` ||
                'unspecified security',
              taxReceipted: true,
              ...trackingData,
              deliveringCustodianInfo,
              securitiesDescription: sec.securitiesDescription,
              securitiesSymbol: sec.fundSymbolCUSIP,
              securitiesQuantity: String(sec.quantity),
            },
            refetchQueries: [
              { query: queries.funds.myFund, variables: { fundId: Number(fundId) } },
              { query: queries.funds.myFundActivity, variables: { fundId: Number(fundId) } },
              { query: queries.paymentMethods.myLinkedBanks, variables: { fundId: Number(fundId) } },
            ],
            awaitRefetchQueries: true,
          })
        )
        const results = await Promise.all(promises)
        const ids = results.map((result) => result.data.createContributionRecord.id)

        setLetterOfDirectionPdf(
          SecuritiesPayment.fetchGeneratedLetterOfDirectionPdf({
            me,
            fundId,
            securities: data?.securities,
            deliveringCustodianInfo: data,
            contributionIds: ids,
          })
        )
        setDialogOpen(true)
      } else {
        throw new Error('Invalid securities data')
      }
    } catch (e) {
      const msg = e.message ?? ''
      if (msg.includes('cannot be invited to the Advisor Portal')) {
        setErrorDialogMessage(
          'This email address cannot be invited to the Advisor Portal. Please try another email address or reach out to support@givewise.ca for assistance.'
        )
      } else {
        setAlert({
          message: `Failed to create a securities contribution: ${msg}`,
          error: msg,
          severity: 'error',
        })
      }
    }
  }

  const submitSecurities = async (data) => {
    // Advisor impersonating: create full contribution records with advisor-led path
    if (isAdvisorImpersonating) {
      const convertedData = {
        ...data,
        securities: data.securities.map((sec) => ({
          ...sec,
          quantity: parseFloat(fromAmountField(sec.quantity, 4)),
          amount: parseFloat(fromAmountField(sec.amount)),
        })),
      }
      setFormData((prevFormData) => ({
        ...prevFormData,
        ...convertedData,
      }))
      createContributionRecords(convertedData, 'advisor-led')
      return
    }

    // Path A: Advisor path (donor working with advisor)
    if (selectedPath === SecuritiesPayment.SECURITIES_PATH.ADVISOR) {
      createAdvisorContributionRequest(data)
      return
    }

    // Path B: Self-directed path
    const convertedData = {
      ...data,
      securities: data.securities.map((sec) => ({
        ...sec,
        quantity: parseFloat(fromAmountField(sec.quantity, 4)),
        amount: parseFloat(fromAmountField(sec.amount)),
      })),
    }
    setFormData((prevFormData) => ({
      ...prevFormData,
      ...convertedData,
    }))
    createContributionRecords(convertedData)
  }

  const onSubmit = async (data) => {
    // A donor can be their own advisor (advisor paths only), but make them confirm the match is intended
    const hasAdvisorEmail = isAdvisorImpersonating || selectedPath === SecuritiesPayment.SECURITIES_PATH.ADVISOR
    if (
      hasAdvisorEmail &&
      data.advisorEmail &&
      me?.email &&
      data.advisorEmail.trim().toLowerCase() === me.email.trim().toLowerCase()
    ) {
      setSelfAdvisorData(data)
      return
    }
    await submitSecurities(data)
  }

  const handleSelfAdvisorInvite = async () => {
    const data = selfAdvisorData
    setSelfAdvisorData(null)
    await submitSecurities(data)
  }

  const closeSuccessDialog = () => {
    resetForm()
    setDialogOpen(false)
    setLetterOfDirectionPdf(null)
  }

  const closeAdvisorDialog = () => {
    resetForm()
    setSelectedPath(null)
    setAdvisorDialogOpen(false)
    setSubmittedIntendedUse(null)
  }

  const renderFormContent = () => {
    // Advisor impersonating client: show direct form (no path selection, no contribution selection)
    if (isAdvisorImpersonating) {
      // Wait for custodian settings to load AND form to be initialized with values
      if (custodianSettingsLoading || !formReady) {
        return <Text.Body>Loading...</Text.Body>
      }

      return (
        <form
          onSubmit={handleSubmit(onSubmit, () => {
            setAlert({
              message: 'Please fill in all required fields.',
              severity: 'error',
            })
          })}
        >
          <Stack>
            <SecuritiesPayment.AdvisorIntendedUseSelect {...{ control, formState }} />
            <SecuritiesPayment.DeliveringCustodianInformation
              {...{ register, control, formState }}
              shrinkLabels
              intendedUse={intendedUse}
            />
            <SecuritiesPayment.SecuritiesToTransferInKind {...{ register, control, formState }} />
            <SecuritiesPayment.Acknowledgement {...{ control, formState }} />

            <Row sx={{ marginTop: '2rem!important' }} alignSelf="flex-end">
              <Button
                type="submit"
                disabled={isSubmitting || !pageLanguageConfirmed}
                data-testid="createSecuritiesContribution"
              >
                Submit
              </Button>
            </Row>
            <Alert {...alertProps} />
          </Stack>
        </form>
      )
    }

    if (!selectedPath) {
      return <SecuritiesPayment.SecuritiesPathSelection onSelect={setSelectedPath} />
    }

    // Path A: Donor works with a financial advisor - show simplified form
    if (selectedPath === SecuritiesPayment.SECURITIES_PATH.ADVISOR) {
      return (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack>
            <SecuritiesPayment.AdvisorDetailsForm {...{ register, control, formState }} />

            <Row sx={{ marginTop: '2rem!important' }} alignSelf="flex-end">
              <Button type="button" color="info" onClick={() => setSelectedPath(null)} sx={{ mr: 2 }}>
                Back
              </Button>
              <Button type="submit" disabled={isSubmitting} data-testid="createSecuritiesContribution">
                Submit
              </Button>
            </Row>
            <Alert {...alertProps} />
          </Stack>
        </form>
      )
    }

    // Path B: Self-directed - show simplified form
    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack>
          <SecuritiesPayment.SelfDirectedCustodianInfo {...{ register, formState }} />
          <SecuritiesPayment.SecuritiesToTransferInKind {...{ register, control, formState }} />
          <SecuritiesPayment.Acknowledgement {...{ control, formState }} />

          <Row sx={{ marginTop: '2rem!important' }} alignSelf="flex-end">
            <Button type="button" color="info" onClick={() => setSelectedPath(null)} sx={{ mr: 2 }}>
              Back
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !pageLanguageConfirmed}
              data-testid="createSecuritiesContribution"
            >
              Submit
            </Button>
          </Row>
          <Alert {...alertProps} />
        </Stack>
      </form>
    )
  }

  return (
    <Container maxWidth="lg">
      <CardBox title="Securities">{renderFormContent()}</CardBox>

      <Text.H5 sx={{ mt: 4, mb: 2 }}>Donating Securities Reduces Tax Compared to Cash – Try Our Tax Calculator</Text.H5>
      <CardBox>
        <Box sx={{ overflow: 'hidden', height: '800px', position: 'relative' }}>
          <iframe
            src="https://taxcalc.givewise.ca/"
            width="100%"
            height="1500px"
            loading="lazy"
            style={{ border: 'none', position: 'absolute', top: '-80px' }}
            title="Charitable Donation Tax Calculator"
          />
        </Box>
      </CardBox>

      <SelfAdvisorConfirmDialog
        open={!!selfAdvisorData}
        subject="the logged in user"
        onBack={() => setSelfAdvisorData(null)}
        onInvite={handleSelfAdvisorInvite}
      />

      <NotificationDialog
        open={dialogOpen}
        onClose={closeSuccessDialog}
        type="success"
        title="Success!"
        message={
          <>
            <p>Thank you for submitting your investment details.</p>
            <p>We&apos;ve sent you the instructions to complete your transfer.</p>
            <p>
              For your convenience, here is a link to download a &quot;Gift of Securities - Letter of Direction&quot;
              that can be shared with your brokerage.
            </p>
            <p>
              Please be aware that a tax receipt will not be populated until the security has been received and is
              liquidated. NOTE: There may be adjustments due to fluctuations in value and exchange.
            </p>
            <SecuritiesPayment.GeneratedLetterOfDirection letterOfDirectionPdf={letterOfDirectionPdf} />
          </>
        }
        actions={[
          {
            label: 'Done',
            onClick: closeSuccessDialog,
            to:
              formData?.intendedUse === 'invest'
                ? `/funds/${fundId}/activity/investment-account`
                : `/funds/${fundId}/activity/giving-wallet`,
          },
        ]}
      />

      <NotificationDialog
        open={advisorDialogOpen}
        onClose={closeAdvisorDialog}
        type="success"
        title="Request Submitted!"
        message={
          <>
            <p>Thank you.</p>
            <p>We&apos;ve contacted your advisor with instructions to initiate the securities transfer to GiveWise.</p>
            <p>If your advisor already works with GiveWise, they&apos;ll be notified of your request.</p>
            <p>If they&apos;re new to GiveWise, they&apos;ll receive an invitation to get set up.</p>
          </>
        }
        actions={[
          {
            label: 'Done',
            onClick: closeAdvisorDialog,
            to:
              submittedIntendedUse === 'liquidate'
                ? `/funds/${fundId}/activity/giving-wallet`
                : `/funds/${fundId}/activity/investment-account`,
          },
        ]}
      />

      <NotificationDialog
        open={!!errorDialogMessage}
        onClose={() => setErrorDialogMessage(null)}
        type="error"
        title="Error"
        message={errorDialogMessage}
        actions={[{ label: 'OK', onClick: () => setErrorDialogMessage(null) }]}
      />

      <NotificationDialog
        open={existingContributionDialogOpen}
        onClose={() => setExistingContributionDialogOpen(false)}
        type="alert"
        title="Outstanding Security Transaction"
        message="There is an outstanding security contribution that has been submitted by the donor. Please complete any outstanding requests before creating a new one."
        actions={[
          {
            label: 'Use Existing',
            onClick: () => navigate(`/funds/${fundId}/contribute/initiated-contributions`),
          },
          {
            label: 'Create New',
            onClick: () => setExistingContributionDialogOpen(false),
            variant: 'text',
          },
        ]}
      />
    </Container>
  )
}
